import { FileOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Form, Input, InputNumber, Select, Upload } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  getInstructors,
  selectAssignableInstructors,
} from "store/slices/instructorsSlice";
import { getSubjects } from "store/slices/subjectsSlice";

const { Option } = Select;
const { TextArea } = Input;

const SupportCourseForm = ({ form, onSubmit, initialValues = {} }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { subjects = [] } = useSelector((state) => state?.subjects);
  const instructors = useSelector(selectAssignableInstructors);
  const { loading } = useSelector((state) => state?.supportCourses);

  const [selectedTypes, setSelectedTypes] = useState([]);
  const [availableStates, setAvailableStates] = useState([]);
  const [selectedStates, setSelectedStates] = useState([]);
  const [previewImage, setPreviewImage] = useState("");
  const [previewVisible, setPreviewVisible] = useState(false);
  const [isParticulierIncluded, setIsParticulierIncluded] = useState(false);

  const educationLevels = ["Primaire", "CEM", "Lycée", "Supérieur"];

  const types = ["online", "atStudent", "atTeacher", "atSchool"];

  const validStates = {
    online: ["particulier", "miniGroupe"],
    atStudent: ["particulier"],
    atTeacher: ["particulier"],
    atSchool: ["particulier", "miniGroupe", "groupe"],
  };

  // Charger les données initiales
  useEffect(() => {
    dispatch(getSubjects());
    dispatch(getInstructors());
  }, [dispatch]);

  // Gérer les valeurs initiales
  useEffect(() => {
    if (initialValues && Object?.keys(initialValues)?.length > 0) {
      form?.setFieldsValue({
        instructorId: initialValues?.instructor?._id,
        subjectId: initialValues?.subject?._id,
        description: initialValues?.description,
        price: initialValues?.price,
        individualPrice: initialValues?.individualPrice,
        educationLevel: initialValues?.educationLevel,
        type: initialValues?.type,
        state: initialValues?.state,
        coverImg: initialValues?.coverImg
          ? [
              {
                uid: "-1",
                name: "Image de couverture",
                status: "done",
                url: initialValues?.coverImg,
              },
            ]
          : [],
        supportCourseRes:
          initialValues?.supportCourseRes?.map((res, index) => ({
            uid: `-${index}`,
            name: `Ressource ${index + 1}`,
            status: "done",
            url: res,
          })) || [],
      });

      if (initialValues?.type) {
        setSelectedTypes(initialValues?.type);
        const newAvailableStates = new Set();
        initialValues?.type?.forEach((type) => {
          validStates[type]?.forEach((state) => newAvailableStates?.add(state));
        });
        setAvailableStates(Array?.from(newAvailableStates));
      }

      // Vérifier si particulier est inclus dans l'état initial
      setIsParticulierIncluded(
        initialValues?.state?.includes("particulier") || false
      );
      setSelectedStates(initialValues?.state || []);
    }
  }, [form, initialValues]);

  // useEffect pour mettre à jour l'état local lorsque l'état du formulaire change
  useEffect(() => {
    const state = form?.getFieldValue("state") || [];
    setIsParticulierIncluded(state?.includes("particulier"));
  }, [form, form?.getFieldValue("state")]);

  const handleTypeChange = (selectedTypes) => {
    setSelectedTypes(selectedTypes);
    const newAvailableStates = new Set();
    selectedTypes?.forEach((type) => {
      validStates[type]?.forEach((state) => newAvailableStates?.add(state));
    });
    setAvailableStates(Array?.from(newAvailableStates));
    form?.setFieldValue("state", []);

    // Réinitialiser isParticulierIncluded car le state a été réinitialisé
    setIsParticulierIncluded(false);
  };

  const normFile = (e) => {
    if (Array?.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  // Fonction pour déterminer si le prix individuel est requis
  const isIndividualPriceRequired = () => {
    return isParticulierIncluded;
  };

  // Fonction pour déterminer si les champs individualPrice doivent être désactivés
  const isIndividualPriceDisabled = () => {
    return !isParticulierIncluded;
  };

  // Gérer la soumission du formulaire
  const handleSubmit = (values) => {
    // Convertir les tags uniquement au moment de la soumission
    if (values?.tags && typeof values?.tags === "string") {
      values.tags = values?.tags?.split(",")?.map((tag) => tag?.trim())?.filter((tag) => tag);
    }

    // Si "particulier" n'est pas inclus, supprimer individualPrice
    if (!values?.state?.includes("particulier")) {
      delete values.individualPrice;
    }

    onSubmit(values);
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      disabled={loading}
    >
      <Form.Item
        name="instructorId"
        label={t("instructors.crud.form.label.name")}
        rules={[
          { required: true, message: t("supportCourse.form.error.instructor") },
        ]}
      >
        <Select>
          {instructors?.map((instructor) => (
            <Option key={instructor?._id} value={instructor?._id}>
              {instructor?.fullName}
            </Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        name="subjectId"
        label={t("supportCourse.crud.form.label.subject")}
        rules={[
          { required: true, message: t("supportCourse.form.error.subject") },
        ]}
      >
        <Select>
          {subjects?.map((subject) => (
            <Option key={subject?._id} value={subject?._id}>
              {subject?.title}
            </Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        name="description"
        label={t("supportCourse.crud.form.label.description")}
        rules={[
          {
            required: true,
            message: t("supportCourse.form.error.description"),
          },
          { max: 200, message: t("supportCourse.form.error.description.max") },
        ]}
      >
        <TextArea rows={4} />
      </Form.Item>
      <Form.Item
        name="educationLevel"
        label={t("supportCourse.crud.form.label.educationLevel")}
        rules={[{ required: true }]}
      >
        <Select>
          {educationLevels?.map((level) => (
            <Option key={level} value={level}>
              {t(`supportCourse.educationLevel.${level}`)}
            </Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        name="type"
        label={t("supportCourse.crud.form.label.type")}
        rules={[{ required: true }]}
      >
        <Select
          mode="multiple"
          onChange={handleTypeChange}
          placeholder={t("supportCourse.type.placeholder")}
        >
          {types?.map((type) => (
            <Option key={type} value={type}>
              {t(`supportCourse.type.${type}`)}
            </Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        name="state"
        label={t("supportCourse.crud.form.label.state")}
        rules={[{ required: true }]}
      >
        <Select
          mode="multiple"
          placeholder={t("supportCourse.state.placeholder")}
          disabled={selectedTypes?.length === 0}
          onChange={(value) => {
            setSelectedStates(value);
            setIsParticulierIncluded(value?.includes("particulier"));
          }}
        >
          {availableStates?.map((state) => (
            <Option key={state} value={state}>
              {t(`supportCourse.state.${state}`)}
            </Option>
          ))}
        </Select>
      </Form.Item>
      {selectedStates?.map((state) => (
        <Form.Item
          key={state}
          name={["price", state]}
          label={`${t("supportCourse.crud.form.label.price")} (${t(
            `supportCourse.state.${state}`
          )})`}
          rules={[
            {
              required: true,
              message: t("supportCourse.form.error.price.required"),
            },
            {
              type: "number",
              min: 0,
              message: t("supportCourse.form.error.price.invalid"),
            },
          ]}
        >
          <InputNumber style={{ width: "100%" }} min={0} addonAfter="DZD" />
        </Form.Item>
      ))}
      {/* 
      <Form.Item
        name="price"
        label={t("supportCourse.crud.form.label.price")}
        rules={[{ required: true, type: "number", min: 0 }]}
      >
        <InputNumber style={{ width: "100%" }} min={0} addonAfter="DZD" />
      </Form.Item> */}
      {/* Individual Price Field */}
      {/* {isParticulierIncluded && (
        <Form.Item
          name="individualPrice"
          label={t("supportCourse.crud.form.label.individualPrice")}
          rules={[
            {
              required: isIndividualPriceRequired(),
              type: "number",
              min: 0,
              message: t("supportCourse.form.error.individualPrice.required"),
            },
          ]}
        >
          <InputNumber style={{ width: "100%" }} min={0} addonAfter="DZD" />
        </Form.Item>
      )} */}
      <Form.Item
        name="coverImg"
        label={t("supportCourse.crud.form.label.coverImage")}
        valuePropName="fileList"
        getValueFromEvent={normFile}
      >
        <Upload
          listType="picture-card"
          maxCount={1}
          beforeUpload={() => false}
          accept="image/*"
        >
          <div>
            <PlusOutlined />
            <div style={{ marginTop: 8 }}>
              {t("supportCourse.crud.form.upload.coverImage")}
            </div>
          </div>
        </Upload>
      </Form.Item>
      <Form.Item
        name="supportCourseRes"
        label={t("supportCourse.crud.form.label.resources")}
        valuePropName="fileList"
        getValueFromEvent={normFile}
      >
        <Upload
          multiple
          beforeUpload={() => false}
          accept=".pdf,.doc,.docx,.ppt,.pptx"
        >
          <Button icon={<FileOutlined />}>
            {t("supportCourse.crud.form.upload.resources")}
          </Button>
        </Upload>
      </Form.Item>
    </Form>
  );
};

export default SupportCourseForm;
